/* eslint-disable @typescript-eslint/camelcase */
import './answers.scss';
import {FunctionalComponent, h} from 'preact';
import {MultipleChoice} from "./MultipleChoice";
import {FocusImperativeInterface, OpenAnswer} from "./OpenAnswer";
import {useEffect, useRef, useState} from "preact/hooks";
import {MultiCheckbox} from "./MultiCheckbox";
import {UploadAnswer} from "./UploadAnswer";
import {PollAnswerMsg} from "./InputUIService";


import {Transition} from 'react-transition-group'
import {log} from "../libs/QuerloLogger";

const animDuration = 200;

/*const animDefaultStyle: any = {
  transition: `opacity ${animDuration}ms ease-in-out`,
  opacity: 0,
}*/
const animDefaultStyle: any = {
  transition: `all ${animDuration}ms ease-in-out`,
  maxHeight: 0,
  opacity: 0
}

const animStyles: any = {
  entering: {
    maxHeight: '150px',
    opacity: 1 },
  entered:  {
    maxHeight: '150px',
    opacity: 1 },
  exiting:  {
    maxHeight: 0,
    opacity: 0},
  exited:  {
    maxHeight: 0,
    opacity: 0},
};


const InputUI: FunctionalComponent<{
    slideState: string;
    question: any;
    voiceInputEnabled: boolean;
    answerCallback: (answerRawObj: PollAnswerMsg) => void;
    hiddenCallback: () => void;
    renderedCallback: (height: number) => void;
    backendBaseURL: string;
    sticky: boolean;
}> =({
     slideState,
     question,
     voiceInputEnabled,
     answerCallback,
     hiddenCallback,
     renderedCallback,
     backendBaseURL,
     sticky,
 }) => {
  
    const buttonsRef = useRef(null);
    const [showInputControlsAboveStickyInput, setShowInputControlsAboveStickyInput] = useState<boolean>(false);
    const answerToBeCasted = useRef<PollAnswerMsg|null>(null);

    const inputFocusImperativeHandler = useRef<FocusImperativeInterface | null>(null);

    /** needed to see updated value inside the animationend event listener */
    const slidingStateRef = useRef<string|null>(null);
    const meRef = useRef<HTMLDivElement>(null);
    slidingStateRef.current = slideState;

    let isCasting = false;
    const classNameBase = 'answers animated';
    const className = slideState === 'IN' ? `${classNameBase} show` : `${classNameBase} hide`;
    let template: JSX.Element|null;

    function doCastAnswer(pollAnswerMsg: PollAnswerMsg|null): void {
      if (isCasting || pollAnswerMsg === null) {
        return;
      }
      isCasting = true; //TODO this check look pretty weak
      answerCallback(pollAnswerMsg);
      isCasting = false;
    }

    function answerCB(pollAnswerMsg: PollAnswerMsg): void {
      if (sticky && showInputControlsAboveStickyInput) {
        setShowInputControlsAboveStickyInput(false);
        answerToBeCasted.current = pollAnswerMsg;
        return;
      }

      doCastAnswer(pollAnswerMsg);
    }

    function sendHeightToParent(): void {
      renderedCallback(meRef.current?.offsetHeight ?? 0);
    }

    useEffect(() => {
        // log.debug(`InputUI initialized ${slideState}`);
        if (meRef.current !== null) {
            meRef.current.addEventListener('animationend', () => {
              log.debug(`[[_INUI__]] animationend callback, sliding state: ${slidingStateRef.current}`);
              if (slidingStateRef.current === 'OUT') {
                   hiddenCallback();
               } else if (question !== null && inputFocusImperativeHandler.current) {
                 inputFocusImperativeHandler.current?.focus();
               }
            });
        }
    }, []);

    useEffect(() => {
      /**
       * when inputUI is sticky, we show the additional input bar (buttons, etc.) through a transition, by
       * setting its trigger through setState. In such cases, the inputUI height passed to parent component through
       * the callback below will only represent the initial value before the transition, and will be updated via
       * transition end callback
       */
      if (sticky &&  (question.questionType === 6 || question.groups || question.questionType === 0)) {
        setShowInputControlsAboveStickyInput(true);
      }
        // log.info(`use effect for ${msgText}`);
        renderedCallback(meRef.current?.offsetHeight ?? 0);
       // alert(meRef.current?.offsetHeight ?? 0);

    }, [question]); // TODO make sure that question is the best option here


    if (question !== null) {
      if (question.questionType === 0) {
        template = <MultipleChoice answerCallback={answerCB} question={question} />;
      }
      else  if (question.questionType === 6) {
        template = <UploadAnswer answerCallback={answerCB} question={question} backendBaseURL={backendBaseURL} />;
      } else if (question.groups) {
        template = <MultiCheckbox question={question} answerCallback={answerCB} />;
      } else {
        template = null;
        //template = <OpenAnswer answerCallback={answerCB} question={question} voiceInputEnabled={voiceInputEnabled}  backendBaseURL={backendBaseURL} />;
      }
    } else {
      return null;
    }

  // 26-Oct-2023 TODO
  // note that in the ternary just below, the condition "sticky ?" might have to be changed to "sticky && template !== null",
  // because there's one case where the Transition is triggered with template = null, but I'd like to test it more thoroughly before making the change
  //
  return (
    <div ref={meRef} className={ `answersWrp ${className}` } aria-hidden={ false } role="dialog">
      {sticky ? <Transition nodeRef={buttonsRef} in={showInputControlsAboveStickyInput} timeout={animDuration}
                            onEntered={sendHeightToParent} onExited={(): void => doCastAnswer(answerToBeCasted.current)}>
        {(state) => {
          log.debug(`Transition state ${state}`);
          return (
            <div ref={buttonsRef} style={{
              overflow: 'hidden',
              ...animDefaultStyle,
              ...animStyles[state]
            }}>
              {template}
            </div>
          );
        }}
      </Transition> : template}
      {(sticky || template === null) && <OpenAnswer answerCallback={answerCB} question={question} voiceInputEnabled={voiceInputEnabled} backendBaseURL={backendBaseURL} focusRef={inputFocusImperativeHandler} />}
    </div>
  );
};

export default InputUI;
